/**
 * OnboardingStep3 — Stile di vita (PR4 Fase 5).
 *
 * Tutti i campi opzionali. Mix di input, textarea, checkbox.
 */

import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import type { OnboardingFormValues } from './onboarding-form-types';
import {
  STATO_CIVILE_OPZIONI,
  STILE_ALIMENTARE_OPZIONI,
} from '@shared/constants/scelte-anagrafica';
import {
  FieldRow,
  SelectField,
  TextArea,
  TextField,
} from '@shared/components/ui/FormField';

interface Step3Props {
  register: UseFormRegister<OnboardingFormValues>;
  errors: FieldErrors<OnboardingFormValues>;
}

export function OnboardingStep3({ register, errors }: Step3Props) {
  const e = errors.step3;
  return (
    <fieldset className="flex flex-col gap-4">
      <legend className="sr-only">Stile di vita</legend>
      <p className="text-sm text-text-secondary">
        Conoscere le tue abitudini ci aiuta a costruire un piano che si adatti
        davvero a te.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldRow
          id="step3-professione"
          label="Professione"
          error={e?.professione?.message}
        >
          {(a) => <TextField {...a} {...register('step3.professione')} />}
        </FieldRow>
        <FieldRow
          id="step3-statoCivile"
          label="Stato civile"
          error={e?.statoCivile?.message}
        >
          {(a) => (
            <SelectField {...a} {...register('step3.statoCivile')}>
              <option value="">Seleziona...</option>
              {STATO_CIVILE_OPZIONI.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </SelectField>
          )}
        </FieldRow>
      </div>

      <FieldRow id="step3-figli" label="Figli" error={e?.figli?.message}>
        {(a) => (
          <TextField
            {...a}
            {...register('step3.figli')}
            type="number"
            inputMode="numeric"
            min={0}
            max={999}
            step={1}
          />
        )}
      </FieldRow>

      <FieldRow
        id="step3-obiettiviConsulenza"
        label="Obiettivi della consulenza"
        hint="Cosa ti aspetti dal percorso?"
        error={e?.obiettiviConsulenza?.message}
      >
        {(a) => <TextArea {...a} {...register('step3.obiettiviConsulenza')} rows={3} />}
      </FieldRow>

      <FieldRow
        id="step3-doveMangia"
        label="Dove mangi solitamente"
        hint="Es. casa, mensa aziendale, ristorante, fuori"
        error={e?.doveMangia?.message}
      >
        {(a) => <TextField {...a} {...register('step3.doveMangia')} />}
      </FieldRow>

      <FieldRow
        id="step3-alimentiSgraditi"
        label="Alimenti che non ti piacciono"
        error={e?.alimentiSgraditi?.message}
      >
        {(a) => <TextArea {...a} {...register('step3.alimentiSgraditi')} rows={2} />}
      </FieldRow>

      <FieldRow
        id="step3-tipoDieta"
        label="Tipo di alimentazione attuale"
        error={e?.tipoDieta?.message}
      >
        {(a) => (
          <SelectField {...a} {...register('step3.tipoDieta')}>
            <option value="">Seleziona...</option>
            {STILE_ALIMENTARE_OPZIONI.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </SelectField>
        )}
      </FieldRow>

      <FieldRow
        id="step3-alcol"
        label="Consumo di alcolici"
        error={e?.alcol?.message}
      >
        {(a) => <TextField {...a} {...register('step3.alcol')} />}
      </FieldRow>

      <FieldRow
        id="step3-momentoFame"
        label="Quando senti piu' fame"
        error={e?.momentoFame?.message}
      >
        {(a) => <TextField {...a} {...register('step3.momentoFame')} />}
      </FieldRow>

      <div className="flex flex-col gap-3 rounded-lg border border-outline-variant bg-surface-container-lowest p-4">
        <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
          <input
            type="checkbox"
            {...register('step3.mangiaVeloce')}
            className="mt-1 h-5 w-5 rounded border-outline text-primary focus:ring-primary"
          />
          <span className="text-sm">Mangio velocemente</span>
        </label>
        <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
          <input
            type="checkbox"
            {...register('step3.fameNervosa')}
            className="mt-1 h-5 w-5 rounded border-outline text-primary focus:ring-primary"
          />
          <span className="text-sm">Mangio per fame nervosa / stress</span>
        </label>
        <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
          <input
            type="checkbox"
            {...register('step3.sport')}
            className="mt-1 h-5 w-5 rounded border-outline text-primary focus:ring-primary"
          />
          <span className="text-sm">Pratico attivita' sportiva</span>
        </label>
      </div>

      <FieldRow
        id="step3-tipoSport"
        label="Tipo di sport (se applicabile)"
        error={e?.tipoSport?.message}
      >
        {(a) => <TextField {...a} {...register('step3.tipoSport')} />}
      </FieldRow>

      <FieldRow
        id="step3-orariSport"
        label="Frequenza / orari sport"
        error={e?.orariSport?.message}
      >
        {(a) => <TextField {...a} {...register('step3.orariSport')} />}
      </FieldRow>

      <FieldRow
        id="step3-altroNote"
        label="Altre note utili per la prima visita"
        error={e?.altroNote?.message}
      >
        {(a) => <TextArea {...a} {...register('step3.altroNote')} rows={3} />}
      </FieldRow>
    </fieldset>
  );
}
